import { Highlight, type PrismTheme } from 'prism-react-renderer'

import { cn } from '@/lib/utils'

import { CopyButton } from './ui/copy-button'

// Light themes matching the Figma "Careforce Design — CORE" code-block specs.
// JSON and SQL color the same Prism token types differently (e.g. a JSON
// string value is green, a SQL string literal is red), so each language gets
// its own theme. Colors reference the design-token palette.
const HIGH_CONTRAST = 'var(--primary-high-contrast)'
const KEYWORD = 'var(--indigo-10)'
const STRING_RED = 'var(--sunset-9)'
const VALUE_GREEN = 'var(--green-9)'
const SQL_IDENTIFIER = 'var(--indigo-9)'
const PLAIN = { color: HIGH_CONTRAST, backgroundColor: 'transparent' }

const jsonTheme: PrismTheme = {
  plain: PLAIN,
  styles: [
    { types: ['property'], style: { color: STRING_RED } },
    { types: ['string'], style: { color: VALUE_GREEN } },
    { types: ['number'], style: { color: VALUE_GREEN } },
    { types: ['boolean', 'null', 'keyword'], style: { color: KEYWORD } },
    { types: ['punctuation', 'operator'], style: { color: HIGH_CONTRAST } },
  ],
}

const sqlTheme: PrismTheme = {
  plain: { color: SQL_IDENTIFIER, backgroundColor: 'transparent' },
  styles: [
    { types: ['keyword', 'function'], style: { color: KEYWORD } },
    { types: ['string'], style: { color: STRING_RED } },
    { types: ['number', 'boolean'], style: { color: VALUE_GREEN } },
    { types: ['punctuation', 'operator'], style: { color: SQL_IDENTIFIER } },
  ],
}

// Fallback (e.g. Python) — a neutral light theme in the same palette.
const defaultTheme: PrismTheme = {
  plain: PLAIN,
  styles: [
    { types: ['keyword', 'function'], style: { color: KEYWORD } },
    { types: ['string'], style: { color: VALUE_GREEN } },
    { types: ['number', 'boolean'], style: { color: VALUE_GREEN } },
    { types: ['comment'], style: { color: 'var(--text-body)' } },
    { types: ['punctuation', 'operator'], style: { color: HIGH_CONTRAST } },
  ],
}

const THEME_BY_LANGUAGE: Record<string, PrismTheme> = {
  json: jsonTheme,
  sql: sqlTheme,
}

interface CodeBlockProps {
  code: string
  language: string
  /** Used for the copy button's accessible label. */
  label: string
  className?: string
  showCopyButton?: boolean
}

// Transparent code renderer — the consumer supplies the light surface
// (`bg-neutral-subtle-bg`/`bg-muted`). Provides syntax highlighting, a
// line-number gutter, and a hover-reveal copy button.
export function CodeBlock({
  code,
  language,
  label,
  className = '',
  showCopyButton = true,
}: CodeBlockProps) {
  const theme = THEME_BY_LANGUAGE[language] ?? defaultTheme

  return (
    <div className={cn('group relative w-full', className)}>
      {showCopyButton && (
        <CopyButton
          content={code}
          label={`Copy ${label}`}
          className="absolute right-0 top-0 z-10 text-text-body opacity-0 transition-opacity hover:text-primary-high-contrast group-hover:opacity-100 focus-visible:opacity-100"
        />
      )}

      <Highlight theme={theme} code={code} language={language}>
        {({ style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className="w-full font-mono text-sm break-words whitespace-pre-wrap border-l border-black-alpha-7"
            style={style}>
            {tokens.map((line, i) => (
              <div
                key={i}
                {...getLineProps({ line })}
                className="flex w-full items-start">
                <span className="mr-3 w-8 shrink-0 pr-2 text-right text-black-alpha-10 select-none">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1 break-words whitespace-pre-wrap">
                  {line.map((token, key) => (
                    <span key={key} {...getTokenProps({ token })} />
                  ))}
                </span>
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  )
}
